import React, { useEffect } from "react";
import { ArrowUpRight, BusFront, LogOut, Map, ShieldCheck } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import BusLoading from "../components/BusLoading";
import { GuideWorkDashboard } from "./DashboardPage";
import DriverDashboardPage from "./Drivers/DriverDashboardPage";
import GuideApplicationPage from "./GuideApplicationPage";
import LoginPage from "./LoginPage";
import RegisterPage from "./RegisterPage";

const mainSiteUrl = "https://uztravell.uz";

export default function ProviderPortalPage({ portalType }) {
  const { t } = useTranslation();
  const { user, loading, logout, refreshProfile } = useAuth();
  const location = useLocation();
  const isDriverPortal = portalType === "driver";
  const targetRole = isDriverPortal ? "driver" : "guide";

  useEffect(() => {
    document.title = isDriverPortal ? t("work_portal.driver_title_document") : t("work_portal.guide_title_document");
  }, [isDriverPortal, t]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const timer = window.setInterval(() => refreshProfile(), 12000);
    return () => window.clearInterval(timer);
  }, [refreshProfile, user?.id]);

  if (loading) return <BusLoading className="bg-[#080a10]" />;

  const Icon = isDriverPortal ? BusFront : Map;
  const title = isDriverPortal ? t("work_portal.driver_title") : t("work_portal.guide_title");
  const isOtherProvider = user && ["driver", "guide"].includes(user.role) && user.role !== targetRole;

  return (
    <div className="min-h-screen bg-[#080a10] text-[#f5f7fa]">
      <header className="border-b border-[#272c35] bg-[#080a10]/95 px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400 text-slate-950"><Icon className="h-5 w-5" aria-hidden="true" /></span>
          <div className="min-w-0 flex-1"><p className="text-xs font-black uppercase tracking-[.18em] text-amber-400">UzTravel · {title}</p>{user && <p className="mt-1 truncate text-sm text-slate-400">{user.name}</p>}</div>
          {mainSiteUrl && <a href={mainSiteUrl} className="hidden items-center gap-2 rounded-full border border-[#353b46] px-4 py-2 text-xs font-bold text-slate-300 transition hover:border-amber-400 hover:text-amber-300 sm:inline-flex">{t("work_portal.main_site")}<ArrowUpRight className="h-3.5 w-3.5" /></a>}
          {user && <button type="button" onClick={logout} aria-label={t("work_portal.logout")} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#353b46] text-slate-300 transition hover:border-amber-400 hover:text-amber-300"><LogOut className="h-4 w-4" /></button>}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        {!user ? (
          <div className="mx-auto max-w-xl">
            <p className="mb-4 flex items-center gap-2 text-xs font-extrabold uppercase tracking-[.18em] text-amber-400"><ShieldCheck className="h-4 w-4" />{t("work_portal.private_portal")}</p>
            {location.pathname === "/register" ? <RegisterPage embedded /> : <LoginPage embedded />}
          </div>
        ) : isOtherProvider ? (
          <section className="mx-auto max-w-2xl rounded-3xl border border-amber-400/20 bg-[#11141b] p-6 sm:p-9">
            <h1 className="text-2xl font-black">{t("work_portal.other_provider_title")}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-400">{t("work_portal.other_provider_text")}</p>
          </section>
        ) : (
          <>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[.18em] text-amber-400"><ShieldCheck className="h-4 w-4" />{t("work_portal.private_portal")}</p>
            <h1 className="mt-2 text-3xl font-black sm:text-4xl">{isDriverPortal ? t("work_portal.driver_heading") : t("work_portal.guide_heading")}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400">{isDriverPortal ? t("work_portal.driver_portal_subtitle") : t("work_portal.guide_portal_subtitle")}</p>
            {isDriverPortal ? <DriverDashboardPage /> : user.role === "guide" && user.is_approved ? <GuideWorkDashboard /> : <GuideApplicationPage />}
          </>
        )}
      </main>
      {!user && mainSiteUrl && <footer className="pb-8 text-center sm:hidden"><a href={mainSiteUrl} className="inline-flex items-center gap-2 text-sm font-bold text-slate-400">{t("work_portal.main_site")}<ArrowUpRight className="h-4 w-4" /></a></footer>}
    </div>
  );
}
